import { useEffect, useRef, useState, useCallback } from "react";
import type { AppState, SaveStatus } from "@/types/state";
import { EMPTY_APP_STATE, loadAppState, saveAppState } from "@/lib/store";

interface StoreContext {
  state: AppState;
  setState: (updater: (s: AppState) => AppState) => void;
  reload: () => Promise<void>;
  saveStatus: SaveStatus;
  ready: boolean;
}

let cached: AppState = EMPTY_APP_STATE;
let ready = false;
let saveStatus: SaveStatus = "idle";
const subs = new Set<() => void>();

function notify() {
  subs.forEach((s) => s());
}

let saveTimer: number | null = null;
function scheduleSave() {
  if (saveTimer !== null) clearTimeout(saveTimer);
  saveStatus = "saving";
  notify();
  saveTimer = window.setTimeout(async () => {
    try {
      await saveAppState(cached);
      saveStatus = "saved";
    } catch (e) {
      console.error("[saveAppState]", e);
      saveStatus = "error";
    }
    notify();
  }, 600);
}

let loadOnce: Promise<void> | null = null;
function ensureLoaded() {
  if (loadOnce) return loadOnce;
  loadOnce = (async () => {
    cached = await loadAppState();
    ready = true;
    notify();
  })();
  return loadOnce;
}

export function useStore(): StoreContext {
  const [, force] = useState(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    const fn = () => {
      if (mountedRef.current) force((n) => n + 1);
    };
    subs.add(fn);
    ensureLoaded();
    return () => {
      mountedRef.current = false;
      subs.delete(fn);
    };
  }, []);

  const setState = useCallback((updater: (s: AppState) => AppState) => {
    cached = updater(cached);
    notify();
    scheduleSave();
  }, []);

  const reload = useCallback(async () => {
    cached = await loadAppState();
    notify();
  }, []);

  return {
    state: cached,
    setState,
    reload,
    saveStatus,
    ready,
  };
}
